import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { Form } from 'components/_forms';
import { Organization, UpdateOrganizationRequest } from 'views/App/Portal/LexID/Organizations/_types/organization';
import useManageOrganizations from 'views/App/Portal/LexID/Organizations/_data_sources/useManageOrganization';
import useOrganization from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganization';
import { validCountries } from '../../Organizations/context';
import { ServerErrorResponse } from 'data_sources/constants';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import errorHelper from 'helpers/errorHelper';

const EditOrganization: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useContext(AppContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [currentCountry, setCurrentCountry] = useState('USA');
  const organizationQuery = useOrganization(currentOrganizationId);

  const { update: updateOrganization } = useManageOrganizations();

  const updateOrganizationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('organizations.properties.name')} is required`),
    country: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('organizations.properties.country')} is required`),
    state: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('organizations.properties.state')} is required`),
    city: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('organizations.properties.city')} is required`),
    salesforceId: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('organizations.properties.salesforceId')} is required`),
    organizationType: Yup.string().trim().required(),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm<UpdateOrganizationRequest>({
    values: {
      name: '',
      city: '',
      country: 'USA',
      salesforceId: '',
      state: '',
      organizationType: 'CUSTOMER',
    },
    resolver: yupResolver(updateOrganizationSchema),
  });

  React.useEffect(() => {
    if (organizationQuery.data?.organization) {
      reset({
        name: organizationQuery.data.organization.name,
        city: organizationQuery.data.organization.city || '',
        country: organizationQuery.data.organization.country || '',
        salesforceId: organizationQuery.data.organization.salesforceId || '',
        state: organizationQuery.data.organization.state || '',
        organizationType: organizationQuery.data.organization.organizationType || 'CUSTOMER',
      });
    }
  }, [reset, organizationQuery.data]);

  const watchCountry = watch('country');

  useEffect(() => {
    setCurrentCountry(watchCountry);
  }, [watchCountry]);

  const handleSave: SubmitHandler<UpdateOrganizationRequest> = (values: UpdateOrganizationRequest) => {
    setIsSaving(true);
    if (currentOrganizationId) {
      updateOrganization(
        currentOrganizationId,
        values,
        (data: Organization) => {
          queryClient.invalidateQueries(['organization', currentOrganizationId]);
          navigate('..');
        },
        (errors: ServerErrorResponse) => {
          setIsSaving(false);
          setServerErrors(errors);
        }
      );
    }
  };

  const states = useMemo(() => {
    return validCountries.find((c) => c.code3 === (currentCountry || 'USA'))?.states || [];
  }, [currentCountry]);

  const pageTitle = `${t('organizations.edit.title')}: ${organizationQuery.data?.organization?.name}`;

  return (
    <TakeoverLayout
      pageTitle={pageTitle}
      backUrl='..'
    >
      <Box sx={{ overflowY: 'auto', minHeight: 0, p: 4 }}>
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        <Form onSubmit={handleSubmit(handleSave)}>
          <CardContent>
            {errorHelper.renderAllErrors(serverErrors)}
            <Grid
              container
              columnSpacing={4}
              rowGap={6}
            >
              <Grid size={{ xs: 12 }}>
                <TextField
                  id='name'
                  key='name'
                  label={t('organizations.properties.name')}
                  variant='outlined'
                  required
                  autoFocus
                  fullWidth
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name ? errors.name.message : undefined}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6 }}
              >
                <Controller
                  name='country'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id='country-label'
                        required
                      >
                        {t('organizations.properties.country')}
                      </InputLabel>
                      <Select
                        id='country'
                        label={t('organizations.properties.country')}
                        value={value}
                        onChange={onChange}
                        error={!!errors.country}
                      >
                        {validCountries.map((country) => (
                          <MenuItem
                            key={`country_${country.code3}`}
                            value={country.code3}
                          >
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.country && <FormHelperText error>{errors.country.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6 }}
              >
                <Controller
                  name='state'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      {states.length > 0 ? (
                        <FormControl fullWidth>
                          <InputLabel
                            id='state-label'
                            required
                          >
                            {t('organizations.properties.state')}
                          </InputLabel>
                          <Select
                            id='state_select'
                            label={t('organizations.properties.state')}
                            value={value}
                            onChange={onChange}
                            error={!!errors.state}
                          >
                            <MenuItem
                              key={`state_empty`}
                              value=''
                            >
                              --
                            </MenuItem>
                            {states.map((state: any) => (
                              <MenuItem
                                key={`state_${state.code}`}
                                value={state.code}
                              >
                                {state.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.state && <FormHelperText error>{errors.state.message}</FormHelperText>}
                        </FormControl>
                      ) : (
                        <TextField
                          id='state'
                          key='state'
                          label={t('organizations.properties.state')}
                          variant='outlined'
                          required
                          fullWidth
                          value={value}
                          onChange={onChange}
                          error={!!errors.state}
                          helperText={errors.state ? errors.state.message : undefined}
                        />
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6 }}
              >
                <TextField
                  id='city'
                  key='city'
                  label={t('organizations.properties.city')}
                  variant='outlined'
                  required
                  fullWidth
                  {...register('city')}
                  error={!!errors.city}
                  helperText={errors.city ? errors.city.message : undefined}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6 }}
              >
                <TextField
                  id='salesforceId'
                  key='salesforceId'
                  label={t('organizations.properties.salesforceId')}
                  variant='outlined'
                  fullWidth
                  {...register('salesforceId')}
                  error={!!errors.salesforceId}
                  helperText={errors.salesforceId ? errors.salesforceId.message : undefined}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <Controller
                    name='organizationType'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <InputLabel>{t('organizations.properties.isTestAgency')}</InputLabel>
                        <Select
                          id='organization_type'
                          label={t('organizations.properties.isTestAgency')}
                          value={value}
                          onChange={onChange}
                        >
                          <MenuItem
                            key='organization_type_test'
                            value='TEST'
                          >
                            {t('common.yes')}
                          </MenuItem>
                          <MenuItem
                            key='organization_type_customer'
                            value='CUSTOMER'
                          >
                            {t('common.no')}
                          </MenuItem>
                        </Select>
                        {errors.organizationType && (
                          <FormHelperText error>{errors.organizationType.message}</FormHelperText>
                        )}
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  trigger();
                }}
                disabled={isSaving}
                component='button'
                type='submit'
                variant='contained'
              >
                {t('organizations.edit.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
      </Card>
      </Box>
    </TakeoverLayout>
  );
};

export default EditOrganization;
